import optionsApi from "./optionsApi";
import { AllocationMatrix } from "../store/useAllocationMatrixStore";

export type CreateMatrixRequest = AllocationMatrix;

export type UpdateMatrixRequest = CreateMatrixRequest & {
  id: string;
};

export type SaveDraftRequest = AllocationMatrix & {
  id?: string;
};

export type ActivateMatrixRequest = AllocationMatrix & {
  id?: string;
};

export const getPlatformFields = () =>
  optionsApi.get("v1/allocationMatrix/platform-fields");

export const getAllMatrix = () => optionsApi.get("v1/allocationMatrix");

export const getMatrixById = (matrixId: string) =>
  optionsApi.get(`v1/allocationMatrix/${matrixId}`);

/** POST /save-draft — create new DRAFT or update existing DRAFT */
export const saveDraft = (data: SaveDraftRequest) =>
  optionsApi.post("v1/allocationMatrix/save-draft", data);

/** POST / — activate: transitions DRAFT→ACTIVE if id provided, else fresh ACTIVE */
export const activateMatrix = (data: ActivateMatrixRequest) =>
  optionsApi.post("v1/allocationMatrix", data);

/** PUT /:matrixId — edit ACTIVE matrix, creates new version and supersedes old */
export const updateMatrix = (matrixId: string, data: UpdateMatrixRequest) =>
  optionsApi.put(`v1/allocationMatrix/${matrixId}`, data);

export const archiveMatrix = (matrixId: string) =>
  optionsApi.post("v1/allocationMatrix/archive", { matrixId });

export const evaluateMatrix = (
  matrixId: string,
  employeeId: string,
  grantId?: string
) =>
  optionsApi.get("v1/allocationMatrix/evaluate", {
    params: { matrixId, employeeId, ...(grantId ? { grantId } : {}) },
  });

/** GET template xlsx with columns: HRID, Employee Name, <factor> */
export const downloadFactorTemplate = (factorId: string): Promise<Blob> =>
  optionsApi
    .get(
      `v1/allocationMatrix/factor-template/${encodeURIComponent(factorId)}`,
      {
        responseType: "blob",
      }
    )
    .then((r) => r.data);

/** GET /:matrixId/export — streams an Excel workbook of all matrix rules */
export const exportMatrix = (matrixId: string): Promise<Blob> =>
  optionsApi
    .get(`v1/allocationMatrix/${matrixId}/export`, { responseType: "blob" })
    .then((r) => r.data);

/** POST — uploads filled template as base64, saves employee metadata,
 *  returns the created AllocationFactor */
export const uploadCustomFactor = async (
  factorId: string,
  file: File
): Promise<import("../store/useAllocationMatrixStore").AllocationFactor> => {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
  const response = await optionsApi.post(
    `v1/allocationMatrix/factor-upload/${encodeURIComponent(factorId)}`,
    { file: base64 }
  );
  return response.data?.data;
};
